import React from 'react';
import {
  Box, Button, Container, Grid, Typography,
} from '@mui/material';
import { Trans } from '@gamiphy/react-i18n';
import { useLoyaltyPageContext } from '../../context';
import { useIsSmallScreen } from '../../hooks';
import CircularStatus from './CircularStatus';
import { Verification } from './verification';
import WelcomeText from './WelcomeText';

const CashbackSection: React.FC = () => {
  const {
    user,
    progress,
    app,
    isMobilePreview,
    welcomeGift,
    tierConfig,
    cashbackProgram: {
      cashbackPercentage,
      minPurchaseAmount,
      bestFitDiscount: {
        value: discountValue,
        minRedemptionAmount,
        isWelcomeGiftDiscount,
      },
    },
  } = useLoyaltyPageContext();

  const isMobile = useIsSmallScreen();

  const description = React.useMemo(() => {
    switch (true) {
      case !user?.unverifiedPhone:
        return (
          <Trans
            i18nKey="loyaltyPage.cashbackProgram.verify.noPhone"
            defaults="Verify your phone {{phone}} to unlock your <strong>{{welcomeGift}} welcome Gift</strong> and start earning <strong>{{cashbackPercentage}}% cashback on every purchase</strong> you make! Secure rewards and savings-get started now!"
            values={{
              welcomeGift,
              cashbackPercentage,
              phone: '',
            }}
          />
        );
      case !user?.phone:
        return (
          <Trans
            i18nKey="loyaltyPage.cashbackProgram.verify.noPhone"
            defaults="Verify your phone {{phone}} to unlock your <strong>{{welcomeGift}} welcome Gift</strong> and start earning <strong>{{cashbackPercentage}}% cashback on every purchase</strong> you make! Secure rewards and savings-get started now!"
            values={{
              welcomeGift,
              cashbackPercentage,
              phone: `${user.unverifiedPhone.slice(0, 10)}***${user.unverifiedPhone.slice(-1)}`,
            }}
          />
        );
      case !!user?.phone:
        if (!discountValue) {
          return (
            <Trans
              i18nKey="loyaltyPage.cashbackProgram.withoutDiscount"
              defaults="<strong>{{cashbackPercentage}}% Cashback is waiting for you!</strong> <br/>Make a purchase over {{minPurchaseAmount, currency}} and earn {{cashbackPercentage}}% Cashback on your next purchase."
              values={{
                cashbackPercentage,
                minPurchaseAmount,
              }}
            />
          );
        }
        if (isWelcomeGiftDiscount) {
          return (
            <Trans
              i18nKey="loyaltyPage.cashbackProgram.withDiscount.welcomeGift"
              defaults="<strong>A {{discountValue}} discount is waiting for you!</strong> <br />Make a purchase of over {{minRedemptionAmount, currency}} to enjoy this discount and receive an additional {{cashbackPercentage}}% Cashback on your next purchase."
              values={{
                discountValue,
                minRedemptionAmount,
                cashbackPercentage,
              }}
            />
          );
        }
        return (
          <Trans
            i18nKey="loyaltyPage.cashbackProgram.withDiscount"
            defaults="<strong>A Cashback of {{discountValue}} is waiting for you!</strong> <br/>Make a purchase of over {{minRedemptionAmount}} to enjoy this cashback and receive an additional {{cashbackPercentage}}% Cashback on your next purchase."
            values={{
              discountValue,
              minRedemptionAmount,
              cashbackPercentage,
            }}
          />
        );
      default:
        return undefined;
    }
  }, [user, welcomeGift, cashbackPercentage, discountValue, minRedemptionAmount, isWelcomeGiftDiscount]);

  return (
    <Box bgcolor="#fff">
      <Container
        maxWidth="lg"
        disableGutters={!isMobile && !isMobilePreview}
        sx={{
          py: {
            xs: 3,
            sm: 5,
          },
          px: 2,
        }}
      >
        <Grid container justifyContent="space-between" rowSpacing={3} columnSpacing={2}>
          <Grid size={(isMobile || isMobilePreview) ? 12 : 6}>
            <Box mb={2}>
              <WelcomeText />
            </Box>
            <Box mb={2}>
              <Typography fontSize={18} fontWeight={400} lineHeight={1.8}>
                {description}
              </Typography>
            </Box>
            {user?.phone ? (
              <Button sx={{ fontSize: 14 }} variant="contained" fullWidth={isMobile || isMobilePreview} onClick={() => { if (app) window.open(app?.domain); }}>
                <Trans i18nKey="shared.shopNow" defaults="Shop Now" />
              </Button>
            ) : (
              <Verification />
            )}
          </Grid>
          {tierConfig?.enabled && (
            <Grid size={(isMobile || isMobilePreview) ? 12 : 6}>
              <CircularStatus progress={progress ?? 0} />
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
};

export default CashbackSection;
